import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { createPageUrl } from '@/utils';
import { Button } from '@/Components/ui/button';
import { Badge } from 'Components/ui/badge';
import { Card, CardContent } from 'Components/ui/card';
import {
    Wrench,
    CheckCircle,
    AlertTriangle,
    Phone,
    Package,
    Search,
    FileText,
    Truck
} from 'lucide-react';
import ContactForm from '@/Components/shared/ContactForm';

const steps = [
    {
        icon: Phone,
        title: 'Contatto',
        description: 'Segnala il guasto tramite il form o telefonicamente'
    },
    {
        icon: Truck,
        title: 'Ritiro',
        description: 'Organizziamo il ritiro dell\'UPS presso la tua sede'
    },
    {
        icon: Search,
        title: 'Diagnosi',
        description: 'Analisi completa e preventivo dettagliato'
    },
    {
        icon: Wrench,
        title: 'Riparazione',
        description: 'Intervento tecnico con ricambi originali'
    },
    {
        icon: CheckCircle,
        title: 'Test',
        description: 'Collaudo completo prima della riconsegna'
    },
    {
        icon: Package,
        title: 'Consegna',
        description: 'Consegna finale post-riparazione'
    },
];

export default function Riparazioni() {
    return (
        <div className="min-h-screen bg-white">
            {/* Hero */}
            <section className="relative bg-gray-900 pt-32 pb-20">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1920&q=80"
                        alt="Tecnico UPS"
                        className="w-full h-full object-cover opacity-20"
                    />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <Badge className="bg-orange-500 text-white mb-6">Assistenza Tecnica</Badge>
                        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                            Riparazione UPS
                        </h1>
                        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                            Il tuo UPS non funziona? Interveniamo rapidamente con diagnosi
                            professionale e riparazione garantita su tutte le marche.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Process Steps */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Come funziona</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Un processo semplice e trasparente per riportare il tuo UPS alla piena operatività
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                        {steps.map((step, index) => (
                            <motion.div
                                key={step.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="relative"
                            >
                                <div className="text-center">
                                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-600 flex items-center justify-center">
                                        <step.icon className="w-8 h-8 text-white" />
                                    </div>
                                    <div className="absolute top-8 left-[calc(50%+2rem)] right-0 h-0.5 bg-blue-200 hidden lg:block last:hidden" />
                                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-600 text-xs font-medium rounded-full mb-2">
                                        Step {index + 1}
                                    </span>
                                    <h3 className="font-semibold text-gray-900 mb-1">{step.title}</h3>
                                    <p className="text-sm text-gray-600">{step.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Form Section */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 items-start">
                        {/* Form */}
                        <motion.div
                            id="segnala-guasto"
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <ContactForm defaultType="riparazione" />
                        </motion.div>

                        {/* Info Cards */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="space-y-6"
                        >
                            <Card className="overflow-hidden">
                                <CardContent className="p-0">
                                    <div className="bg-orange-500 p-6 text-white">
                                        <AlertTriangle className="w-8 h-8 mb-3" />
                                        <h3 className="text-xl font-semibold mb-2">Guasto Urgente?</h3>
                                        <p className="text-orange-100 mb-4">
                                            Per emergenze chiama direttamente il nostro numero dedicato
                                        </p>
                                        <a href="tel:+393296139684">
                                            <Button className="bg-white text-orange-600 hover:bg-orange-50 rounded-full gap-2">
                                                <Phone className="w-4 h-4" />
                                                329 6139684
                                            </Button>
                                        </a>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardContent className="p-6">
                                    <h3 className="font-semibold text-gray-900 mb-4">Perché scegliere Tecnosys?</h3>
                                    <ul className="space-y-3">
                                        {[
                                            'Tecnici certificati su tutte le marche',
                                            'Diagnosi gratuita e preventivo trasparente',
                                            'Ricambi originali garantiti',

                                            'Tracking in tempo reale nell\'Area Clienti'
                                        ].map((item) => (
                                            <li key={item} className="flex items-start gap-2">
                                                <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                                                <span className="text-gray-600">{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardContent className="p-6">
                                    <h3 className="font-semibold text-gray-900 mb-4">Hai già un ticket aperto?</h3>
                                    <p className="text-gray-600 mb-4">
                                        Accedi all'Area Clienti per monitorare lo stato della tua riparazione in tempo reale.
                                    </p>
                                    <div className="flex flex-col items-center gap-2">
                                        <Link to={createPageUrl('AreaClienti')} className="w-full">
                                            <Button variant="outline" className="w-full rounded-full gap-2">
                                                <FileText className="w-4 h-4" />
                                                Accedi all'Area Clienti
                                            </Button>
                                        </Link>
                                        <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full uppercase tracking-wider">
                                            Coming Soon
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
}